// ANGEL FLIGHT — crew comms script + pacing tunables.
//
// The G2 research call: crew chatter IS the genre's feel carrier (AC-130 /
// Zombie Gunship both lean on it). All authored lines live here — the
// CrewComms director (audio/CrewComms.ts) picks them round-robin per category
// with cooldowns, so the crew never machine-guns the same phrase. NO VO
// assets: lines render on the HUD comms ticker and each one fires a procedural
// radio-squelch blip in the AudioBus.

/** One comms category: an authored line set + its own repeat cooldown. */
export interface CommsCategory {
  /** Authored lines, cycled round-robin (never the same line twice in a row). */
  lines: readonly string[];
  /** Min ms between two lines OF THIS CATEGORY. */
  cooldownMs: number;
  /** Urgent lines queue while the global gap holds (instead of dropping). */
  urgent?: boolean;
}

export const COMMS = {
  /** Min ms between ANY two comms lines (radio discipline — no wall of text). */
  globalGapMs: 2600,
  /** How long a line holds on the HUD ticker before fading. */
  holdMs: 3400,
  /** The HUD speaker tag. */
  speaker: 'CREW',

  categories: {
    killConfirm: {
      cooldownMs: 7000,
      lines: [
        'Good kill, good kill.',
        'Target down.',
        "He's down.",
        'Splash one.',
        'Got him.',
        'Good effect on target.',
      ],
    },
    headshot: {
      cooldownMs: 9000,
      lines: [
        'Beautiful shot.',
        'Clean hit, clean hit.',
        'Right on the X.',
      ],
    },
    streak: {
      cooldownMs: 12000,
      lines: [
        "They're dropping — keep it up.",
        "You're mowing them down.",
        'Keep the gun talking.',
      ],
    },
    waveContact: {
      cooldownMs: 5000,
      lines: [
        'Contact — multiple hostiles inbound.',
        'More coming. Stay on the gun.',
        "New push, twelve o'clock.",
        'Second wave on the treeline.',
      ],
    },
    telegraph: {
      cooldownMs: 8000,
      lines: [
        'Launcher sighted — take him first.',
        'RPG on the standoff line!',
        'Shooter holding back — drop him.',
      ],
    },
    hullDamage: {
      cooldownMs: 6000,
      lines: [
        "We're taking fire!",
        "They're biting the bird — burn them down!",
        "Hull's taking hits back here.",
      ],
    },
    hullCritical: {
      cooldownMs: 10000,
      urgent: true,
      lines: [
        "We're hit BAD — clear that strike line NOW.",
        'Bird won\'t take much more. Hose the close ones!',
      ],
    },
    friendlyHit: {
      cooldownMs: 4000,
      urgent: true,
      lines: [
        'CHECK FIRE — friendlies in the open!',
        'Watch your fire — blue on the ground!',
        'Cease fire, cease fire — friendlies!',
      ],
    },
    friendlyWarning: {
      cooldownMs: 3000,
      urgent: true,
      lines: ['CHECK FIRE! One more strike and we are DONE.'],
    },
    friendlyDown: {
      cooldownMs: 4000,
      urgent: true,
      lines: ['Friendly down… friendly down. Hold fire.', 'We hit one of ours. God.'],
    },
    dry: {
      cooldownMs: 9000,
      lines: ["You're dry — venting.", "Gun's empty, stand by."],
    },
    cleared: {
      cooldownMs: 0,
      urgent: true,
      lines: ['Area clear. Good work, Angel.', 'That\'s the last of them. RTB.'],
    },
    failed: {
      cooldownMs: 0,
      urgent: true,
      lines: ['Angel-7 is hit… Angel-7 is going down.'],
    },
  },
} as const satisfies {
  globalGapMs: number;
  holdMs: number;
  speaker: string;
  categories: Record<string, CommsCategory>;
};

export type CommsCategoryId = keyof typeof COMMS.categories;
